import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUnitsTypesData } from '../../../store/stationsAndUnitsReducer/selectors';
import ButtonTheme from '../../../components/ThemeButton/ThemeButton';

const UnitForm = ({ unit, onSubmit, onCancel, isEdit = false, stationId }) => {
  const { unitsTypesEnum, isUnitsTypesEnumLoading } = useSelector(selectUnitsTypesData);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      staffing: 0,
      dedicated_staffing: false,
      cad_designation_1: '',
      cad_designation_2: '',
      type: '',
    },
  });

  const dedicatedStaffing = watch('dedicated_staffing');
  const watchedType = watch('type');

  useEffect(() => {
    if (unit) {
      // Set form values when unit data is provided
      setValue('staffing', unit.staffing || 0);
      setValue('dedicated_staffing', unit.dedicated_staffing || false);
      setValue('cad_designation_1', unit.cad_designation_1 || '');
      setValue('cad_designation_2', unit.cad_designation_2 || '');
      setValue('type', unit.type || '');
    }
  }, [unit, setValue]);

  const handleFormSubmit = (data) => {
    // Convert staffing to number if it's a string
    const formData = {
      ...data,
      staffing: parseInt(data.staffing) || 0,
    };
    onSubmit(formData);
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  // Disable submit button if form is not dirty (for edit mode)
  const isSubmitDisabled = isSubmitting || (isEdit && !isDirty);

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {isEdit ? 'Edit Unit' : 'Create New Unit'}
      </Typography>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid container spacing={3}>
          {/* CAD Designation 1 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="CAD Designation 1"
              {...register('cad_designation_1', {
                required: 'CAD Designation 1 is required',
              })}
              error={!!errors.cad_designation_1}
              helperText={errors.cad_designation_1?.message}
            />
          </Grid>

          {/* CAD Designation 2 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="CAD Designation 2"
              {...register('cad_designation_2', {
                required: 'CAD Designation 2 is required',
              })}
              error={!!errors.cad_designation_2}
              helperText={errors.cad_designation_2?.message}
            />
          </Grid>

          {/* Type */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={unitsTypesEnum || []}
              value={watchedType || null}
              onChange={(event, newValue) => {
                setValue('type', newValue || '', { shouldValidate: true });
              }}
              onInputChange={(event, newInputValue) => {
                if (newInputValue && !unitsTypesEnum?.includes(newInputValue)) {
                  setValue('type', '', { shouldValidate: true });
                }
              }}
              loading={isUnitsTypesEnumLoading}
              disabled={isUnitsTypesEnumLoading}
              freeSolo={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type"
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  {...register('type', {
                    validate: (value) => {
                      if (value && !unitsTypesEnum?.includes(value)) {
                        return 'Please select a valid type from the list';
                      }
                      return true;
                    },
                  })}
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option) => option || ''}
            />
          </Grid>

          {/* Staffing */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Staffing"
              {...register('staffing', {
                min: { value: 0, message: 'Staffing must be 0 or greater' },
                valueAsNumber: true,
              })}
              error={!!errors.staffing}
              helperText={errors.staffing?.message}
            />
          </Grid>

          {/* Dedicated Staffing */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  {...register('dedicated_staffing')}
                  checked={dedicatedStaffing}
                  onChange={(e) => setValue('dedicated_staffing', e.target.checked)}
                />
              }
              label="Dedicated Staffing"
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <ButtonTheme
                type="submit"
                variant="filled"
                color="primary"
                disabled={isSubmitDisabled}
              >
                {isSubmitting ? 'Saving...' : isEdit ? 'Update Unit' : 'Create Unit'}
              </ButtonTheme>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default UnitForm;
